import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Our Approach',
  sub_title: '取り組み',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'sectionTitle',
      text: '業務フロー全体を再構築 ー 属人化を解消し、拡大に強い仕組みへ',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/nakajitsu/approach01.webp',
      alt: '業務フロー全体を再構築 ー 属人化を解消し、拡大に強い仕組みへ',
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'Before：紙と各人のスキルに依存した不確実性の高い業務フロー',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            これまで不動産SHOP
            ナカジツでは、営業スタッフが現地・役所での調査内容を紙の物件調査票に手書きで記入し、その後、契約書作成システムへ手動で転記するという二重作業が発生していました。
          </p>
          <p>
            <span className={styles.space_top}>
              さらに、支店長による紙面でのチェックや不備があった際には外部のチャットツール経由で差し戻すといった属人的な確認フローが並行しており、情報の抜け漏れや記入ミス、承認の遅れが発生しやすい体制になっていました。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              このように、紙の調査票・システム転記・人によるチェックが分断されていたため、業務全体の効率性と品質が安定せず、教育コストや管理負担の増大も課題となっていました。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'greyBox',
      name: '具体的な課題',
      texts: [
        <>
          <p>
            <span className={styles.titleSpaceTop}>
              ■ <span className={styles.textMiddle}>若手スタッフによる記入漏れ・誤記の多発</span>
            </span>
            <br />
            物件調査票の記入内容に若手社員の慣れ・知識不足による漏れ・誤りが目立っており、紙での記入からシステム入力という二重の手間・転記ミスのリスクが高かった。
          </p>
          <p>
            <span className={styles.titleSpace}>
              ■ <span className={styles.textMiddle}>チェック品質のばらつき（管理者のスキル依存）</span>
            </span>
            <br />
            契約書チェックは専門知識を要し、内容が複雑なため、チェック担当者のスキルにより承認基準・修正率が変動。人員・スキルにより品質の平準化が難しかった。
          </p>
          <p>
            <span className={styles.titleSpace}>
              ■ <span className={styles.textMiddle}>チェック人員の不足</span>
            </span>
            <br />
            店舗・エリアが拡大する中で、チェックにあたる管理者・支店長の人員確保が追いつかず、若手の記入ミスを発見・修正するリソースが限られていた。
          </p>
          <p>
            <span className={styles.titleSpace}>
              ■ <span className={styles.textbold}>差戻しの多発による業務停滞</span>
            </span>
            <br />
            記入漏れや誤記による差戻しが多く発生し、1件あたりの再処理時間が長く、契約書作成〜契約締結までのリードタイム増加、さらには購入者へのスピード対応という観点でもリスクが生まれていた。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            これらの課題は、物件調査票作成から契約書作成・承認に至る一連の流れが「複雑かつ属人的」である業界特有の構造を背景としており、「記入・転記・チェック・訂正」の工程が多数の条件分岐を含んでいるため、若手のスキル・管理者の経験・人員配置のいずれかの不足によってフロー全体が停滞してしまうという状況に陥っていました。{' '}
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'After：電子化と自動連携により、スキルに依存しないスムーズな業務フローへ',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            そこでAitriaは、これらの工程を一気通貫でつながるデジタルフローへ再構築しました。物件調査票を電子化し、入力内容が自動で契約書に反映される仕組みを導入することで、これまで手作業により発生していた転記ミスを解消しました。さらに、経験の少ないスタッフでも正しく入力できるよう、適切な箇所でヘルプやアラートを表示し、記入漏れや誤りを防止するとともに、教育面の支援にもつなげました。
          </p>
          <p>
            <span className={styles.space_top}>
              また、チェックや承認もシステム上で完結できるように改善し、管理者が正確かつスピーディーに内容を確認できるようになったことで、承認スピードと品質の両立を実現。フロー全体の効率化とともに、店舗拡大・人員増加にも対応できる再現性のある運用体制を確立しました。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/nakajitsu/approach02.webp',
      alt: 'これまで別ツールで行っていた担当者への差戻しを同じシステム内で完結できるように改善。コメント箇所や解決/未解決も一目瞭然で管理できるように。',
      text: 'これまで別ツールで行っていた担当者への差戻しを同じシステム内で完結できるように改善。コメント箇所や解決/未解決も一目瞭然で管理できるように。',
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '業務現場の丁寧な読み解きから導いた直感的UI',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            プロジェクトの初期フェーズに、デザイナーとエンジニアを含むプロジェクトメンバーにて、実際に現場に同行し業務の様子を視察させていただきました。視察では、物件調査において確認すべきポイント、承認フロー、そしてミスや差戻しが起こりやすい箇所などに焦点を当て、調査票作成から契約書承認に至る一連のフローの解像度を高めました。
          </p>
          <p>
            <span className={styles.space_top}>
              その上で、UI設計においては「OOUI（Object Oriented User Interface）※」の考え方を取り入れました。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'greyBox',
      texts: [
        <>
          <p>
            ※OOUIとは、ユーザーが操作対象となる“オブジェクト”（＝モノ・対象）を起点にUIを設計する手法で、「まずオブジェクトを選び、その後にアクションを選択する」という構造を特徴としています。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            システムの画面では、「案件」という単位を操作の対象（オブジェクト）として設計しました。
            <br />
            物件ごとに進む調査から契約書の承認までの流れを、この「案件」単位でひとまとめに管理することで、営業担当・管理者いずれも直感的に操作できる画面構造にしています。
          </p>
          <p>
            <span className={styles.space_top}>
              「物件」単位でも構築可能でしたが、「契約承認まで一貫して進める」という目的から、「案件」を起点にする方が分かりやすく操作性が高いと判断しました。
              <br />
              また、営業スタッフが現地での物件調査や役所での確認作業など、出先や屋外で操作するケースが多いことを想定し、スマートフォン画面でも快適に利用できるように画面設計を行いました。
              <br />
              特に、日光下での利用や短時間での情報入力が求められる物件調査画面においては、屋外でも視認性を確保できるようにコントラスト比を高めた色使いを採用し、操作ミスを防ぐためのアクセシビリティにも配慮しています。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/nakajitsu/approach03.webp',
      alt: '物件調査票の入力画面では、説明を補足し記入ミスを防止。さらにスマートフォンでも使いやすい画面構成としました。',
      text: '物件調査票の入力画面では、説明を補足し記入ミスを防止。さらにスマートフォンでも使いやすい画面構成としました。',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            プロジェクト進行の面においては、デザイン意図を丁寧に共有することで、システム担当者だけでなく業務担当者とも認識を細かくすり合わせながら進めました。実際に業務を行う現場担当の方にも、定例会議に参加いただき、デザイン・開発チーム間で理解にズレが生まれないようなコミュニケーションを重視しました。
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '複雑な条件分岐を整理し、正確なシステム設計を実現',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            開発面においては、多数の条件分岐や既存システムとの連携を正確に整理し、仕様理解のずれを防ぐために業務フローを可視化しました。今回の開発で特に難しかったのは、契約書による必要事項の多岐にわたる条件分岐を正しく把握し、それをシステムに落とし込むことでした。
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/nakajitsu/approach04.webp',
      alt: '条件分岐ごとに業務フロー図を整理',
      text: '条件分岐ごとに業務フロー図を整理',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            併せて、既存のシステムとの連携も複雑だったため、連携ポイントを分かりやすく可視化し、不動産SHOP
            ナカジツの方々と共通認識を持つことが重要なポイントとなりました。
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/nakajitsu/approach05.webp',
      alt: '他システムとの連携を可視化',
      text: '他システムとの連携を可視化',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            条件分岐が膨大だった点はテストフェーズにおいても乗り越えるべき大きな課題であり、テスト項目に抜け漏れがないよう、条件ごとに詳細なドキュメントをまとめて整理し、システム担当者と密に連携しながら品質を担保しました。
          </p>
          <p>
            <span className={styles.space_top}>
              また、本プロジェクトは将来的な内製化を見据えた開発体制でもあり、ソースコードの可読性や拡張性などコード品質にも配慮しました。クラス設計や命名規則、コメントの構造化など、後続のエンジニアが保守・機能追加を容易に行えるように実装ルールを統一。コードレビューやドキュメント整備も丁寧に行い、社内開発チームがスムーズに引き継げる状態で納品させていただきました。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              こうした取り組みを通じて、複雑な業務フローや業界特有の規約を正確に理解し、それらを踏まえたUIやシステム構成を構築しました。業界特有の知識が求められる場面や、既存の業務フローが複雑なケースにも対応できるよう、丁寧な設計と検証を重ねながら開発を進めました。
            </span>
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '同業他社への展開も見据え、社内運用を開始',
    },
    {
      type: 'textBox',
      texts: [
        <>
          <p>
            本システムは現在、社内での本格運用を進めています。今後は、他システムとの連携やさらなる自動化機能の実装、対象となる書類や業務範囲の拡大といったフェーズ2の開発を開始する予定で、より高い効果の創出を目指します。また、将来的には同業他社への外販も視野に入れており、不動産業界全体の業務効率化・品質向上の支援へと発展させていく予定です。
          </p>
        </>,
      ],
      wide: true,
      bold: false,
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'クライアントの声',
    },
    {
      type: 'greyBox',
      namesub: '株式会社不動産SHOP ナカジツ システム企画課',
      name: '此島 功一郎氏',
      texts: [
        <>
          <p>
            今回デジタル化した業務は、画面項目が多く専門知識も必要で、難易度の高い業務でした。そのため、UX/UIを改善し、少しでもストレスなくわかりやすい業務にしたいと考え、Aitria様に協力を依頼しました。細部のUI一つ一つが重要となる中、常に高いレベルの提案をいただいたおかげで、プロジェクトはスムーズに進みました。
          </p>
          <p>
            <span className={styles.space_top}>
              また、自動化処理や条件分岐が多く技術設計の難易度が高いシステムでしたが、保守性や将来の機能拡張も見据えた設計をご提案いただき、使いやすさと機能性を両立したシステムを完成させることができました。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              今後もさまざまな社内システム開発を企画しており、他の業務領域の新システム開発においても、ぜひAitria様にお願いしたいと考えています。
            </span>
          </p>
        </>,
      ],
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 90%',
      toggleActions: 'play none none play',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
